<template>
  <v-card
    outlined
    rounded="lg"
    class="indicator-card pa-4"
    elevation="0"
  >
    <!-- Title bar with optional Excel button and info tooltip -->
    <div v-if="title" class="indicator-card__header">
      <div class="d-flex align-center justify-space-between">
        <div class="d-flex align-center" style="gap: 4px;">
          <h3 v-if="title" class="indicator-card__title-text">{{ $t(title) }}</h3>
          <v-tooltip v-if="title && description" bottom max-width="320">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on">
                <v-icon small color="#5A6678">mdi-information-outline</v-icon>
              </v-btn>
            </template>
            <span>{{ description }}</span>
          </v-tooltip>
        </div>
        <v-tooltip v-if="!hideExcel" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              depressed
              small
              class="secondary-button"
              @click="$emit('export-excel')"
              :loading="excelLoading"
              :disabled="!excelEndpoint"
              v-bind="attrs"
              v-on="!excelEndpoint ? on : {}"
            >
              <v-icon small>mdi-microsoft-excel</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('Exportação Excel indisponível') }}</span>
        </v-tooltip>
      </div>
      <v-divider class="my-3"></v-divider>
    </div>

    <!-- Optional date range header (hidden in result/PDF mode) -->
    <div v-if="showDates && !isResult" class="indicator-card__dates">
      <span class="indicator-card__dates-label">{{ $t('Período') }}</span>
      <div class="d-flex justify-center align-center mt-3">
        <slot name="date-range" />
      </div>
      <v-divider class="my-4"></v-divider>
    </div>

    <!-- Loading state — circular spinner centered inside a rounded container -->
    <div
      v-if="loading"
      class="indicator-card__loading"
      :style="{ minHeight: loadingHeight }"
    >
      <v-progress-circular
        indeterminate
        size="48"
        width="4"
        color="primary"
      />
      <span class="indicator-card__loading-text mt-3">
        {{ $t('Carregando...') }}
      </span>
    </div>

    <!-- No-data warning -->
    <v-alert
      v-else-if="!hasData"
      type="warning"
      outlined
      rounded="lg"
      class="my-3 indicator-card__no-data"
    >
      {{ noDataMessage || $t('Nenhum dado encontrado') }}
    </v-alert>

    <!-- Main content -->
    <div v-else class="indicator-card__content">
      <slot />
    </div>

    <!-- Critical analysis (review) editor -->
    <slot name="review" />
  </v-card>
</template>

<script>
export default {
  name: 'IndicatorCard',

  props: {
    loading: { type: Boolean, default: false },
    hasData: { type: Boolean, default: true },
    isResult: { type: Boolean, default: false },
    showDates: { type: Boolean, default: true },
    title: { type: String, default: '' },
    description: { type: String, default: '' },
    excelEndpoint: { type: String, default: '' },
    hideExcel: { type: Boolean, default: false },
    excelLoading: { type: Boolean, default: false },
    noDataMessage: { type: String, default: '' },
    loadingHeight: { type: String, default: '320px' },
  },
}
</script>

<style scoped>
.indicator-card {
  border-radius: 12px !important;
  border-color: #e0e5e9 !important;
  background: #ffffff;
  transition: box-shadow 0.2s ease;
  overflow: hidden;
}

.indicator-card:hover {
  box-shadow: 0 4px 16px rgba(15, 53, 101, 0.06);
}

.indicator-card__title {
  margin-bottom: 4px;
}

.indicator-card__title-text {
  color: #0f3565;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.indicator-card__dates {
  text-align: center;
}

.indicator-card__dates-label {
  font-size: 13px;
  color: #5a6678;
  font-weight: 500;
}

.indicator-card__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: linear-gradient(180deg, #fafbfc 0%, #f4f6f8 100%);
  border: 1px dashed #e0e5e9;
  margin: 8px 0;
  padding: 24px;
}

.indicator-card__loading-text {
  font-size: 13px;
  color: #5a6678;
  font-weight: 500;
}

.indicator-card__no-data {
  border-radius: 8px !important;
}

.indicator-card__content {
  border-radius: 12px;
  overflow: hidden;
}

/* Rounded corners on any nested chart canvases */
.indicator-card__content :deep(canvas),
.indicator-card__content :deep(.echarts) {
  border-radius: 8px;
  overflow: hidden;
}

/* ── Polished v-data-table styling ────────────────────────────────────── */
.indicator-card__content :deep(.v-data-table) {
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #eef1f4;
  background: #ffffff;
}

.indicator-card__content :deep(.v-data-table > .v-data-table__wrapper > table > thead > tr > th) {
  background-color: #0f3565;
  color: #ffffff !important;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.2px;
  border-bottom: none !important;
  padding: 12px 16px !important;
}

.indicator-card__content :deep(.v-data-table > .v-data-table__wrapper > table > tbody > tr > td) {
  padding: 10px 16px !important;
  border-bottom: 1px solid #f0f3f5 !important;
  color: #2c3e50;
  font-size: 13px;
}

/* Zebra striping — only for rows without inline background (preserves colored rows) */
.indicator-card__content :deep(.v-data-table > .v-data-table__wrapper > table > tbody > tr:nth-child(even):not([style*="background"])) {
  background-color: #fafbfc;
}

/* Hover state — only for rows without inline background (preserves colored rows) */
.indicator-card__content :deep(.v-data-table > .v-data-table__wrapper > table > tbody > tr:not([style*="background"]):hover) {
  background-color: #f0f4f8 !important;
}

/* Last row no border */
.indicator-card__content :deep(.v-data-table > .v-data-table__wrapper > table > tbody > tr:last-child > td) {
  border-bottom: none !important;
}

/* Pagination polish */
.indicator-card__content :deep(.v-data-footer) {
  border-top: 1px solid #eef1f4 !important;
  background: #fafbfc;
}

/* Pagination wrapper */
.indicator-card__content :deep(.pagination) {
  display: flex;
  justify-content: center;
  margin-top: 12px;
}

.indicator-card__content :deep(.v-pagination__item),
.indicator-card__content :deep(.v-pagination__navigation) {
  border-radius: 8px !important;
  box-shadow: none !important;
  border: 1px solid #e0e5e9;
}

.indicator-card__content :deep(.v-pagination__item--active) {
  background-color: #0f3565 !important;
  color: #ffffff !important;
}
</style>
